import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import Pagination from '@mui/material/Pagination';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { memo, useId, useMemo } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { ROUTES } from '@/app/routes';
import { CardSkeleton } from '@/components/ui/Skeletons';
import { RowActionsMenu, type RowAction } from '@/components/ui/RowActionsMenu';
import { StatusChip } from '@/components/ui/StatusChip';
import { formatDate } from '@/lib/organization';
import { testIdPart } from '@/lib/testIds';

import { ACTION_LABELS, actionsFor, type PageActionId } from './pageActions';
import {
  pageViewsLabel,
  placementWord,
  stateTone,
  stateWord,
  type PageRow,
} from './pageLabels';
import { PAGE_SIZE } from './usePages';

/** Two columns from `md`, one below (P7). */
const GRID = {
  display: 'grid',
  gap: 2,
  gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
  listStyle: 'none',
  p: 0,
  m: 0,
} as const;

/** Placeholder cards while the first page loads. */
const LOADING_CARDS = 4;

/** The menu's writes, off while any page write is in flight (P19). */
const WRITES = new Set<PageActionId>(['publish', 'unpublish', 'remove']);

interface PageCardProps {
  row: PageRow;
  writable: boolean;
  busy: boolean;
  onAction: (action: PageActionId, row: PageRow) => void;
}

function PageCard({ row, writable, busy, onAction }: PageCardProps) {
  const headingId = useId();
  const part = testIdPart(row.id);
  const actions = useMemo<RowAction[]>(
    () =>
      actionsFor(row, writable).map((id) => ({
        id,
        label: ACTION_LABELS[id],
        disabled: busy && WRITES.has(id),
        onSelect: () => onAction(id, row),
      })),
    [row, writable, busy, onAction],
  );

  return (
    <li>
      <Paper
        component="article"
        variant="outlined"
        aria-labelledby={headingId}
        data-testid={`pages-card-${part}`}
        sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column', gap: 1 }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <StatusChip
            label={stateWord(row.lifecycle)}
            tone={stateTone(row.lifecycle)}
            testId={`pages-card-${part}-status-chip`}
          />
          <Typography
            variant="body2"
            color="text.secondary"
            data-testid={`pages-card-${part}-placement-value`}
          >
            {placementWord(row.inArc)}
          </Typography>
          <Box sx={{ ml: 'auto' }}>
            {actions.length > 0 ? (
              <RowActionsMenu
                rowLabel={row.title}
                actions={actions}
                testId="pages-card-actions"
              />
            ) : null}
          </Box>
        </Box>
        <Typography id={headingId} component="h2" variant="h6">
          <Link
            component={RouterLink}
            to={ROUTES.pages.edit(row.id)}
            underline="hover"
            color="inherit"
            data-testid={`pages-card-${part}-title-link`}
          >
            {row.title}
          </Link>
        </Typography>
        {row.summary ? (
          <Typography
            variant="body2"
            color="text.secondary"
            data-testid={`pages-card-${part}-summary-value`}
            sx={{
              display: '-webkit-box',
              WebkitLineClamp: 3,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {row.summary}
          </Typography>
        ) : null}
        <Box
          sx={{
            mt: 'auto',
            display: 'flex',
            justifyContent: 'space-between',
            gap: 1,
            flexWrap: 'wrap',
          }}
        >
          <Typography variant="body2" data-testid={`pages-card-${part}-views-value`}>
            {pageViewsLabel(row.views, row.lifecycle === 'DRAFT')}
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            data-testid={`pages-card-${part}-updated-value`}
          >
            Updated {formatDate(row.updatedAt)}
          </Typography>
        </Box>
      </Paper>
    </li>
  );
}

interface PageCardsProps {
  rows: PageRow[];
  /** The first page has not arrived: placeholder cards in the grid. */
  loading: boolean;
  page: number;
  total: number;
  onPageChange: (page: number) => void;
  writable: boolean;
  /** A page write is in flight: the menu's writes wait for it (P19). */
  busy?: boolean;
  onAction: (action: PageActionId, row: PageRow) => void;
}

/**
 * The Custom Pages list as cards (PB2, P7): a list of articles, each named by
 * its title link (PB15), with its menu from `actionsFor` (P10), and the pager
 * under them. Memoised, and fed stable props by the page, so a keystroke in
 * the search box does not redraw the cards (scalability rule 4, PNF1).
 */
export const PageCards = memo(function PageCards({
  rows,
  loading,
  page,
  total,
  onPageChange,
  writable,
  busy = false,
  onAction,
}: PageCardsProps) {
  const pages = Math.ceil(total / PAGE_SIZE);

  if (loading) {
    return (
      <Box component="ul" sx={GRID} aria-busy="true" data-testid="pages-list-loading">
        {Array.from({ length: LOADING_CARDS }, (_, index) => (
          <li key={index}>
            <CardSkeleton label={index === 0 ? 'Loading pages' : undefined} />
          </li>
        ))}
      </Box>
    );
  }

  return (
    <>
      <Box component="ul" sx={GRID} aria-label="Custom pages" data-testid="pages-list">
        {rows.map((row) => (
          <PageCard
            key={row.id}
            row={row}
            writable={writable}
            busy={busy}
            onAction={onAction}
          />
        ))}
      </Box>
      {pages > 1 ? (
        <Pagination
          count={pages}
          page={page}
          onChange={(_, next) => onPageChange(next)}
          aria-label="Pages"
          data-testid="pages-pagination"
          sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}
        />
      ) : null}
    </>
  );
});
